import React, { Suspense, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FloatingBobaPearls from './components/FloatingBobaPearls';
import Header from './components/Header';
import MobileFooter from './components/MobileFooter';
import DesktopFooter from './components/DesktopFooter';
import AuthPromptModal from './components/AuthPromptModal';
import MiniKitProvider from './components/MiniKitProvider';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './components/ToastContainer';
import { BalanceRefreshProvider } from './contexts/BalanceRefreshContext';
import { AssetLayersProvider } from './contexts/AssetLayersContext';
import { WorldAppProvider, useWorldApp } from './contexts/WorldAppContext';
import { BgmProvider } from './contexts/BgmContext';
import EventPosterModal from './components/EventPosterModal';
import { EVENT_POSTER_DISMISS_KEY, EVENT_POSTER_SIGNATURE } from './components/eventPosterConfig';

// 使用 React.lazy 進行程式碼分割，帶有錯誤重試機制
type LazyLoadedModule = { default: React.ComponentType };

const lazyWithRetry = (componentImport: () => Promise<LazyLoadedModule>) =>
  React.lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.localStorage.getItem('page-has-been-force-refreshed') || 'false'
    );

    try {
      const component = await componentImport();
      window.localStorage.setItem('page-has-been-force-refreshed', 'false');
      return component;
    } catch (error) {
      console.error('Failed to load module:', error);

      if (!pageHasAlreadyBeenForceRefreshed) {
        console.log('Refreshing page to reload modules...');
        window.localStorage.setItem('page-has-been-force-refreshed', 'true');
        window.location.reload();
      }

      throw error;
    }
  });

const LandingPage = lazyWithRetry(() => import('./pages/LandingPage'));
const ProfilePage = lazyWithRetry(() => import('./pages/ProfilePage'));
const DexPage = lazyWithRetry(() => import('./pages/DexPage'));
const WalletPage = lazyWithRetry(() => import('./pages/Wallet'));
const BBGClaimPage = lazyWithRetry(() => import('./pages/BBGclaim'));
const GamePage = lazyWithRetry(() => import('./pages/Game'));
const ThroneBattlePage = lazyWithRetry(() => import('./pages/ThroneBattlePage'));
const CharacterLibraryPage = lazyWithRetry(() => import('./pages/CharacterLibrary'));
const ItemInventoryPage = lazyWithRetry(() => import('./pages/ItemInventory'));
const ShopPage = lazyWithRetry(() => import('./pages/ShopPage'));
const MiningMapPage = lazyWithRetry(() => import('./pages/MiningMapPage'));
const MiningRegionEditorPage = lazyWithRetry(() => import('./pages/MiningRegionEditorPage'));
const CharacterSynthesisPage = lazyWithRetry(() => import('./pages/CharacterSynthesisPage'));
const AppearancePage = lazyWithRetry(() => import('./pages/AppearancePage'));
const LeaderboardPage = lazyWithRetry(() => import('./pages/LeaderboardPage'));
const AuthCallbackPage = lazyWithRetry(() => import('./pages/AuthCallback'));
const HandleOAuthPage = lazyWithRetry(() => import('./pages/HandleOAuthPage'));
const PrivacyPolicyPage = lazyWithRetry(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazyWithRetry(() => import('./pages/TermsOfServicePage'));
const FurnacePage = lazyWithRetry(() => import('./pages/FurnacePage'));
const RelicFusionPage = lazyWithRetry(() => import('./pages/RelicFusionPage'));
const StoryChapterPage = lazyWithRetry(() => import('./pages/StoryChapterPage'));

// 載入中組件
const LoadingFallback = () => {
  const { t } = useTranslation();
  const [loadingTime, setLoadingTime] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setLoadingTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // 如果載入超過 10 秒,顯示重新整理按鈕
  if (loadingTime > 10) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-start pt-[30vh] px-4">
        <img
          src="https://hmseykssvtyrihuxymeo.supabase.co/storage/v1/object/public/bbt/public/image/logo/bbg_token.png"
          alt="Loading"
          className="w-20 h-20 mb-6"
        />
        <p className="text-gray-400 text-center mb-4">{t('common.loadingTimeout')}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-semibold transition-colors"
        >
          {t('common.reload')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-start pt-[30vh]">
      <div className="w-full flex justify-center">
        <img
          src="https://hmseykssvtyrihuxymeo.supabase.co/storage/v1/object/public/bbt/public/image/logo/bbg_token.png"
          alt="Loading"
          className="w-20 h-20 animate-spin"
          style={{
            animation: 'spin 2s linear infinite'
          }}
        />
      </div>
    </div>
  );
};

const App = () => {
  console.log('✅ [App] App component rendering...');

  return (
    <MiniKitProvider>
      <WorldAppProvider>
        <BgmProvider>
        <BalanceRefreshProvider>
          <ToastProvider>
            <AuthProvider>
              <AssetLayersProvider>
                <ErrorBoundary>
                  <Router>
                    <AppContent />
                  </Router>
                </ErrorBoundary>
              </AssetLayersProvider>
            </AuthProvider>
          </ToastProvider>
        </BalanceRefreshProvider>
        </BgmProvider>
      </WorldAppProvider>
    </MiniKitProvider>
  );
};

const AppContent = () => {
  const location = useLocation();
  const { user, isLoading, signIn } = useAuth();
  const { isWorldApp } = useWorldApp();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isPosterOpen, setIsPosterOpen] = React.useState(false);
  const [isAuthPromptOpen, setIsAuthPromptOpen] = React.useState(false);
  
  console.log('✅ [App] AppContent component rendering...');

  // 路由變化時滾動到頂部
  useEffect(() => {
    // 當路由改變時，滾動到頁面頂部
    if (contentRef.current) {
      contentRef.current.scrollTo(0, 0);
    }
  }, [location.pathname]);

  // 登入後延遲顯示活動彈窗
  useEffect(() => {
    let timeoutId: number | undefined;

    if (!isLoading && user) {
      const dismissedSignature = localStorage.getItem(EVENT_POSTER_DISMISS_KEY);
      if (dismissedSignature !== EVENT_POSTER_SIGNATURE) {
        timeoutId = window.setTimeout(() => setIsPosterOpen(true), 700);
      }
    } else {
      setIsPosterOpen(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [user, isLoading]);

  const handlePosterClose = () => setIsPosterOpen(false);

  const handleRequireLogin = () => setIsAuthPromptOpen(true);

  const handleAuthPromptLogin = () => {
    setIsAuthPromptOpen(false);
    void signIn().catch(() => {
      // AuthContext already exposes the login error through its toast flow.
    });
  };
  
  return (
    <div className="fixed inset-0 w-full h-full" style={{ overscrollBehavior: 'none' }}>
      {/* 浮動珍珠背景 - 在所有頁面顯示 */}
      <FloatingBobaPearls />

      {/* Header 固定在頂部 */}
      <div className="fixed top-0 left-0 right-0 z-50 fixed-element" style={{ overscrollBehavior: 'none' }}>
        <Header isWorldApp={isWorldApp} onRequireLogin={handleRequireLogin} />
      </div>

      {/* 主體內容，使用 padding 預留 Header/MobileFooter 空間 */}
      <div
        ref={contentRef}
        className="absolute inset-0 overflow-x-hidden overflow-y-auto content-scrollable app-content-inset"
        style={{
          overscrollBehavior: 'none',
          WebkitOverflowScrolling: 'touch',
          willChange: 'scroll-position'
        }}
      >
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/handle-oauth" element={<HandleOAuthPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />

            {/* Protected routes */}
            <Route element={<PrivateRoute />}>
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/dex" element={<DexPage />} />
              <Route path="/wallet" element={<WalletPage />} />
              <Route path="/bbg-claim" element={<BBGClaimPage />} />
              <Route path="/game" element={<GamePage />} />
              <Route path="/throne-battle" element={<ThroneBattlePage />} />
              <Route path="/characters" element={<CharacterLibraryPage />} />
              <Route path="/items" element={<ItemInventoryPage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/mining-map" element={<MiningMapPage />} />
              <Route path="/mining-region-editor" element={<MiningRegionEditorPage />} />
              <Route path="/synthesis" element={<CharacterSynthesisPage />} />
              <Route path="/appearance" element={<AppearancePage />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              <Route path="/furnace" element={<FurnacePage />} />
              <Route path="/relic-fusion" element={<RelicFusionPage />} />
              <Route path="/story/:chapterNumber" element={<StoryChapterPage />} />
            </Route>
          </Routes>
        </Suspense>
      </div>

      {/* Mobile Footer 固定在底部 */}
      <div className="fixed bottom-0 left-0 right-0 z-50 sm:hidden fixed-element" style={{ overscrollBehavior: 'none' }}>
        <MobileFooter onRequireLogin={handleRequireLogin} />
      </div>

      {/* Desktop Footer 固定在底部，登入後隱藏 */}
      {!user && (
        <div className="fixed bottom-0 left-0 right-0 z-50 hidden sm:block fixed-element" style={{ overscrollBehavior: 'none' }}>
          <DesktopFooter onRequireLogin={handleRequireLogin} />
        </div>
      )}

      <AuthPromptModal
        isOpen={isAuthPromptOpen && !user}
        isWorldApp={isWorldApp}
        onClose={() => setIsAuthPromptOpen(false)}
        onLogin={handleAuthPromptLogin}
      />

      <EventPosterModal
        isOpen={isPosterOpen}
        onClose={handlePosterClose}
      />
    </div>
  );
};

export default App;
